import { Link, NavLink } from "react-router";

import { NavigationArtifact } from "@/assets/background-patterns/navigation-artifact";
import { OrgControlLogo } from "@/assets/logos/org-control-logo";
import { NAV, type NavItem, ROUTES } from "@/constants/routes";
import { useShadowProfile } from "@/features/auth/use-shadow-profile";
import { MobileNavigationHeader } from "@/features/navigation/components/mobile-header";
import { NavAccountCard } from "@/features/navigation/components/nav-account-card";
import { NavOrgCard } from "@/features/navigation/components/nav-org-card";
import { cx } from "@/utils/cx";

/**
 * Left-hand menu. Organization-scoped items on top, global items below,
 * account card pinned to the bottom (color theme, org switch, sign out).
 *
 * Items marked `requiresGlobalAccess` are hidden rather than disabled: the API
 * enforces the same flag on every route behind them, so there is nothing
 * for a viewer without the grant to reach. An external configurator sees
 * only the top group, and no "Global" heading at all.
 */
export const Sidebar = () => {
	const { globalAccess } = useShadowProfile();
	const visible = (items: readonly NavItem[]) =>
		items.filter((item) => !item.requiresGlobalAccess || globalAccess);

	const organization = visible(NAV.organization);
	const global = visible(NAV.global);

	// Fresh trees for mobile vs desktop so each gets its own NavigationArtifact
	// (unique gradient ids) and React doesn’t move one subtree between parents.
	const renderContent = () => (
		<aside className="relative flex h-full min-h-dvh w-(--width-main-sidebar) min-w-65 flex-col justify-between pt-4 lg:min-h-0 lg:pt-5">
			{/* Scroll the nav content, not the aside — overflow on the aside
			    isolates blend modes from the drawer/sidebar backdrop. */}
			<div className="relative z-10 flex min-h-0 flex-1 flex-col justify-between overflow-auto">
				<div className="flex flex-col gap-5 px-4">
					<Link
						className="text-lg font-semibold tracking-tight text-white"
						to={ROUTES.ORGANIZATION_LOCATIONS}
					>
						<OrgControlLogo />
					</Link>
					<NavOrgCard />
				</div>

				<div className="mt-5 flex min-h-0 flex-1 flex-col gap-6 overflow-auto px-2">
					<NavGroup items={organization} label="Organization" />
					{global.length > 0 && <NavGroup items={global} label="Global" />}
				</div>

				<div className="mt-auto shrink-0 px-2 py-4 lg:px-4">
					<NavAccountCard />
				</div>
			</div>
			<NavigationArtifact />
		</aside>
	);

	return (
		<>
			<MobileNavigationHeader>{renderContent()}</MobileNavigationHeader>

			<div className="hidden lg:fixed lg:top-0 lg:bottom-0 lg:left-0 lg:flex">
				{renderContent()}
			</div>

			{/* Placeholder: desktop sidebar is `fixed`, so it needs layout width. */}
			<div
				className="invisible hidden lg:sticky lg:top-0 lg:bottom-0 lg:left-0 lg:block"
				style={{ paddingLeft: "calc(var(--width-main-sidebar))" }}
			/>
		</>
	);
};

interface NavGroupProps {
	items: readonly NavItem[];
	label: string;
}

const NavGroup = ({ items, label }: NavGroupProps) => (
	<nav aria-label={label} className="flex flex-col px-2">
		<h2 className="px-3 pb-1 text-xs font-semibold tracking-wide text-white/50 uppercase">
			{label}
		</h2>
		<ul className="flex flex-col">
			{items.map((item) => (
				<li className="py-0.5" key={item.href}>
					<NavLink
						className={({ isActive }) =>
							cx(
								"group relative flex w-full cursor-pointer items-center rounded-md px-3 py-2 text-md font-semibold transition duration-100 ease-linear select-none",
								isActive
									? "bg-white/10 text-white"
									: "text-white/80 hover:bg-white/10 hover:text-white",
							)
						}
						to={item.href}
					>
						{item.label}
					</NavLink>
				</li>
			))}
		</ul>
	</nav>
);
